import { Clock, Lock, Phone } from "lucide-react";
import "./IntakeForm.css";

export const INTAKE_SLUGS = [
  "intake-consent",
  "intake-consent-ndis",
  "consent-only",
  "consent-exchange-info",
  "ndis-service-agreement-adult",
  "ndis-service-agreement-early-childhood",
];

const CONTENT = {
  "intake-consent": {
    heading: "New client intake and consent",
    topline:
      "This form collects your details and your consent to start working with Clinical Minds Psychology. It explains how your information is kept private, fees, telehealth, and the tools we use to help with record keeping.",
    time: "About 10 to 15 minutes",
    button: "Start my intake form",
  },
  "intake-consent-ndis": {
    heading: "New client intake and consent (NDIS)",
    topline:
      "This form collects your details, your NDIS information and your consent to start working with Clinical Minds Psychology. It explains how your information is kept private, telehealth, and the tools we use to help with record keeping. Fees are covered separately in your NDIS service agreement.",
    time: "About 10 to 15 minutes",
    button: "Start my intake form",
  },
  "consent-only": {
    heading: "Your yearly consent review",
    topline:
      "Once a year we ask you to read through how we work together and how your information is handled, and confirm you're still happy with it. Nothing has to change.",
    time: "About 5 minutes",
    button: "Review and sign",
  },
  "consent-exchange-info": {
    heading: "Permission to share information",
    topline:
      "This form lets you choose who Lachlan can talk to about your care (or your child's care), such as a school or GP, and what can be shared. You stay in control and can change your mind at any time.",
    time: "About 5 minutes",
    button: "Choose who we can talk to",
  },
  "ndis-service-agreement-adult": {
    heading: "NDIS service agreement",
    topline:
      "This agreement sets out the supports Clinical Minds provides under your NDIS plan, what they cost, and how payments and cancellations work. It's needed before NDIS-funded supports begin.",
    time: "About 10 minutes",
    button: "Review and sign the agreement",
  },
  "ndis-service-agreement-early-childhood": {
    heading: "NDIS service agreement",
    topline:
      "This agreement sets out the supports Clinical Minds provides under your NDIS plan, what they cost, and how payments and cancellations work. It's needed before NDIS-funded supports begin.",
    time: "About 10 minutes",
    button: "Review and sign the agreement",
  },
};

const STEPS = [
  { title: "Read through", sub: "Take your time" },
  { title: "Fill in and sign", sub: "Sign with your finger or mouse" },
  { title: "Sent to Clinical Minds", sub: "A copy is kept in your file" },
];

export default function IntakeLanding({ slug, onStart, loading }) {
  const c = CONTENT[slug] || CONTENT["intake-consent"];
  return (
    <div className="il-page" data-testid={`intake-landing-${slug}`}>
      <div className="il-card">
        <section className="il-left">
          <div className="il-kicker">CLINICAL MINDS PSYCHOLOGY</div>
          <h1 className="il-heading">Before you start</h1>
          <div className="il-rule" />
          <div className="il-time">
            <Clock size={16} /> {c.time}
          </div>
          <p className="il-topline">{c.topline}</p>

          <div className="il-subhead">Not sure about something?</div>
          <p className="il-help-intro">You don't need to sign anything you don't understand. Ask us first:</p>
          <ul className="il-help-list">
            <li>
              Email <a href="mailto:contact@clinicalminds.net">contact@clinicalminds.net</a>
            </li>
            <li>
              Call or text <a href="tel:0415220944">0415 220 944</a>
            </li>
            <li>Or talk it through with Lachlan at your next session</li>
          </ul>

          <div className="il-steps">
            {STEPS.map((s, i) => (
              <div className="il-step" key={i}>
                <div className="il-step-num">{i + 1}</div>
                <div>
                  <div className="il-step-title">{s.title}</div>
                  <div className="il-step-sub">{s.sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="il-phonehelp">
            <Phone size={14} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} />
            Stuck partway through? Call or text <a href="tel:0415220944">0415 220 944</a> and we'll help.
          </div>
        </section>

        <section className="il-right">
          <h2 className="il-form-heading">{c.heading}</h2>
          <p className="il-form-line">When you're ready, start below. There's nothing to fill in first.</p>
          <button className="il-btn" onClick={onStart} disabled={loading} data-testid="intake-landing-start-button">
            {loading ? "Loading…" : c.button}
          </button>
          <p className="il-privacy">
            <Lock size={14} />
            <span>Your form goes only to Clinical Minds and is stored securely in Australia.</span>
          </p>
        </section>
      </div>
    </div>
  );
}
